/** @jsx jsx */
import { Flex, Input, Button, jsx } from 'theme-ui'
import { Search } from '../../icons/index'
import { navigate } from 'gatsby'
import { useState, useEffect } from 'react'
import { bool, func, string } from 'prop-types'
import { getSearchQueryOnLoad } from './HelperFunctions'

const H = 44 // single source of truth for height

const CustomSearchBar = ({
  defaultSearchTerm = '',
  setSearchTerm,
  searchRedirect = false,
  placeholderText = 'Search Resources',
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(
    getSearchQueryOnLoad(
      defaultSearchTerm,
      typeof window !== 'undefined' ? window.location.search : ''
    )
  )

  const saveSearchToLocal = () => {
    let recentSearches = JSON.parse(localStorage.getItem('searches'))
    if (localSearchTerm) {
      if (!recentSearches) recentSearches = []
      const existTerm = recentSearches.includes(localSearchTerm)
      if (existTerm) {
        const index = recentSearches.indexOf(localSearchTerm)
        recentSearches.splice(index, 1)
      }
      recentSearches.unshift(localSearchTerm)
      recentSearches = recentSearches.slice(0, 5)
      localStorage.setItem('searches', JSON.stringify(recentSearches))
    }
  }

  const pushSearchToDataLayer = () => {
    window.dataLayer?.push({
      event: 'view_search_results_custom',
      search_term: localSearchTerm,
    })
  }

  const onSearchClick = searchRedirect
    ? searchTerm => {
        saveSearchToLocal(searchTerm)
        navigate('/app/ResourceSearch', {
          state: {
            searchTerm: searchTerm.substring(searchTerm.indexOf(':') + 1),
          },
        })
        pushSearchToDataLayer()
      }
    : () => {
        setSearchTerm(localSearchTerm)
        saveSearchToLocal()
        pushSearchToDataLayer()
      }

  useEffect(() => {
    if (typeof setSearchTerm === 'function') {
      if (!localSearchTerm) setSearchTerm('')
    }
  }, [localSearchTerm])

  return (
    <Flex
      as="form"
      onSubmit={e => {
        e.preventDefault()
        onSearchClick(localSearchTerm)
      }}
      sx={{
        backgroundColor: 'white',
        border: '1px solid #ccc',
        height: `${H}px`,
        width: '100%',
        maxWidth: 390,
        minWidth: 215,
        position: 'relative',
      }}
    >
      <Input
        data-automation-id="custom-search-input"
        sx={{
          // exact sizing
          height: `${H}px`,
          width: '100%',
          boxSizing: 'border-box',

          lineHeight: `${H}px`,
          paddingTop: '10px',
          paddingBottom: '10px',

          // keep space for the button on the right
          paddingLeft: '16px',
          paddingRight: `${H + 8}px`,

          // reset quirks
          border: 'none',
          outline: 'none',
          boxShadow: 'none',
          borderRadius: 0,
          backgroundColor: 'transparent',
          WebkitAppearance: 'none',
          appearance: 'none',

          // typography + horizontal align
          fontSize: 2,
          fontFamily:
            'Graphik, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
          textAlign: 'left',

          // native placeholder will now sit in the same vertical center
          '::placeholder': {
            color: '#888',
            opacity: 1,
            textAlign: 'left',
          },
        }}
        value={localSearchTerm}
        autoComplete="off"
        placeholder={placeholderText}
        type="text"
        aria-label="search"
        onChange={e => setLocalSearchTerm(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
            onSearchClick(localSearchTerm)
          }
        }}
      />

      <Button
        data-automation-id="custom-search-button"
        onClick={e => {
          //automation id
          e.preventDefault()
          onSearchClick(localSearchTerm)
        }}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          height: `${H}px`,
          width: `${H}px`,
          minWidth: `${H}px`,
          minHeight: `${H}px`,
          borderRadius: 0,
          backgroundColor: 'skyblue',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          border: 'none',
          cursor: 'pointer',
          p: 2,
          overflow: 'hidden',
        }}
        type="submit"
        aria-label="search"
      >
        <Search color="black" height={20} width={20} />
      </Button>
    </Flex>
  )
}

CustomSearchBar.propTypes = {
  defaultSearchTerm: string,
  setSearchTerm: func.isRequired,
  searchRedirect: bool,
  placeholderText: string,
}

export default CustomSearchBar
